import React from 'react';
import { Container, Typography, Box, Paper, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import RouterIcon from '@mui/icons-material/Router';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CellTowerIcon from '@mui/icons-material/CellTower';
import ConstructionIcon from '@mui/icons-material/Construction';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PowerIcon from '@mui/icons-material/Power';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorageIcon from '@mui/icons-material/Storage';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';

const TelecomServices: React.FC = () => {
  // Service areas
  const serviceAreas = [
    {
      title: 'Network Operation Center',
      description: 'Full-scale NOC management with 24/7 monitoring and support.',
      icon: <RouterIcon fontSize="large" color="primary" />
    },
    {
      title: 'Field Operation Management',
      description: 'Comprehensive field operations and maintenance services.',
      icon: <EngineeringIcon fontSize="large" color="primary" />
    },
    {
      title: 'Maintenance Services',
      description: 'Routine, corrective, and preventive maintenance of BTS, Transmission and Power Systems.',
      icon: <ConstructionIcon fontSize="large" color="primary" />
    },
    {
      title: 'Network Design & Implementation',
      description: 'Expert design and implementation services for telecom and IT networks.',
      icon: <AccountTreeIcon fontSize="large" color="primary" />
    }
  ];

  // Telecom capabilities
  const capabilities = [
    {
      title: 'Network Design & Implementation',
      description: 'Expert services for fixed, wireless, and IT network roll-outs and upgrades.'
    },
    {
      title: 'Fiber & IT Infrastructure',
      description: 'Design, installation, and commissioning of fiber, IT & IP network solutions.'
    },
    {
      title: 'Power Systems Management',
      description: 'Comprehensive maintenance and optimization of telecom power systems.'
    },
    {
      title: 'BTS & Transmission Systems',
      description: 'Specialized services for base transceiver stations and transmission networks.'
    },
    {
      title: 'Database Management',
      description: 'Professional database solutions tailored for telecom operations.'
    },
    {
      title: 'Network Optimization',
      description: 'Performance analysis and optimization for maximum efficiency.'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4, 
            alignItems: 'center'
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                  Managed Services for Telecom Sector
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, fontWeight: 'normal' }}>
                  Comprehensive telecom operations and maintenance solutions
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="/contact"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: '50px',
                    background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CellTowerIcon sx={{ fontSize: 180, opacity: 0.8 }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6 
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                Telecom Operations & Maintenance
              </Typography>
              <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                Operations and maintenance are routine actions, which keep the network in working order (known as scheduled maintenance) or prevent trouble from arising (preventive maintenance). The actions include the combination of all technical and corresponding administrative, managerial, and supervision actions.
              </Typography>

              <Typography variant="body1" paragraph>
                Our managed services include Network Operation Center (NOC), Field Operation Management (FM) and Service Network O&M, Routine Maintenance, corrective maintenance & preventive maintenance of BTS, Transmission and Power Systems etc. We communicate with customers, understand and handle their problems, and prepare reports based on site visits and investigations.
              </Typography>
              
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                Our Expertise
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                {[
                  'Telecom and IT Network design & Implementation services',
                  'Experienced and certified professionals with years of industry experience',
                  'Expertise in Fixed, Wireless and IT Network roll-outs and upgrades',
                  'Network Operations and Database Management Systems',
                  'Website and Desktop Development for telecom applications'
                ].map((expertise, index) => (
                  <Box 
                    key={index} 
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <CheckCircleOutlineIcon color="primary" />
                    <Typography variant="body1">{expertise}</Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1562408590-e32931084e23?ixlib=rb-1.2.1&auto=format&fit=crop&w=1374&q=80"
                alt="Telecom Services"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Service Areas Section */}
      <Box sx={{ py: 10, background: '#f5f7fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h4"
              component="h2"
              textAlign="center"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Our Managed Services
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
            >
              Comprehensive telecom services to maintain and optimize your network infrastructure
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}>
            {serviceAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>{area.icon}</Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight="bold"
                    color="primary"
                  >
                    {area.title}
                  </Typography>
                  <Typography variant="body2">
                    {area.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Fiber & IT Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 6
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(40% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <ConnectWithoutContactIcon sx={{ fontSize: 200, color: '#1976d2', opacity: 0.8 }} />
              </Box>
            </motion.div>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(60% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                Fiber & IT Network Services
              </Typography>
              <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
              <Typography variant="body1" paragraph>
                Right Track works as a professional with its reputable clients to provide IT & Fiber services. We have rich experience in both IT and Fiber network design & deployment. Our experienced professional staff has worked in a wide array of platforms, languages, and tools during our various projects.
              </Typography>
              <Typography variant="body1" paragraph>
                We provide Fiber, IT & IP Network Engineering, Design, Installation, and Commissioning services. Using a consultative approach, our team of highly skilled IT professionals work with clients to understand the needs of the business both now and in the future, listening to requirements before offering advice.
              </Typography>
              <Typography variant="body1" paragraph>
                Whatever the size of the organization, we understand that client's IT is critical and as a business we aim to concentrate on technical excellence so that our clients can focus on running their core business functions with confidence.
              </Typography>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Capabilities Section */}
      <Box sx={{ py: 10, background: '#f5f7fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h4"
              component="h2"
              textAlign="center"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Our Telecom Capabilities
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
            >
              A wide range of telecom and IT expertise to meet your specific needs
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 4
          }}>
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    {index === 0 ? <NetworkCheckIcon fontSize="large" /> :
                     index === 1 ? <SettingsInputComponentIcon fontSize="large" /> :
                     index === 2 ? <PowerIcon fontSize="large" /> :
                     index === 3 ? <CellTowerIcon fontSize="large" /> :
                     index === 4 ? <StorageIcon fontSize="large" /> :
                     <RouterIcon fontSize="large" />}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h3" fontWeight="bold" color="primary" gutterBottom>
                      {capability.title}
                    </Typography>
                    <Typography variant="body1">
                      {capability.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Need telecom solutions?
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              Partner with our expert team to maintain, optimize, and advance your telecom infrastructure
            </Typography>
            <Button
              variant="contained"
              size="large"
              component="a"
              href="/contact"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                background: 'white',
                color: '#0c2340',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get in Touch
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default TelecomServices;